import { forwardRef, memo, useEffect, useImperativeHandle, useRef, useState } from "react";
import { LobbyScreen } from "./Lobby/LobbyScreen";
import TableScreen from "./TrucoTable/TableScreen";
import "./Truco.scss"
import gsap from "gsap"

/**
 * Events that carry a `time` field (timer duration in seconds).
 * When replaying after tab-hidden gap, we subtract elapsed hidden time
 * so the timer starts at the correct remaining value instead of the original.
 */
const TIMER_EVENTS = new Set(["TIMER_START"])

/**
 * Events that are meaningless to replay if the game has already moved past them.
 * Stale instances of these are dropped when the queue is fast-forwarded.
 */
const DROPPABLE_STALE_EVENTS = new Set([
    "PLAYER_ACTIONS",
    "TIMER_START",
])

const Truco = forwardRef((props, ref) => {

    useImperativeHandle(ref, () => ({
        checkGameResponse: handleSocketData
    }), [])

    const [currentTableId, setCurrentTableId] = useState(
        localStorage.getItem("tableId") || undefined
    )
    const [currentTableDetails, setCurrentTableDetails] = useState(
        JSON.parse(localStorage.getItem("tableDetails")) || undefined
    )
    const lobbbyRef = useRef()
    const tableRef = useRef()
    const eventQueueRef = useRef([])
    const isProcessingRef = useRef(false)

    /**
     * Timestamp when the tab became hidden.
     * Used to calculate how much time elapsed while hidden so we can
     * fast-forward timers instead of replaying them from scratch.
     */
    const hiddenAtRef = useRef(null)

    useEffect(() => {
        props.assestsLoaderFunction()

        const handleVisibility = () => {
            if (document.hidden) {
                onTabHidden()
            } else {
                onTabVisible()
            }
        }

        document.addEventListener("visibilitychange", handleVisibility)
        return () =>
            document.removeEventListener("visibilitychange", handleVisibility)
    }, [])

    // ─── Tab lifecycle ────────────────────────────────────────────────────────

    const onTabHidden = () => {
        hiddenAtRef.current = Date.now()
        // Pause the queue — events will continue to accumulate in eventQueueRef
        isProcessingRef.current = true
    }

    const onTabVisible = () => {
        const hiddenDurationMs = hiddenAtRef.current
            ? Date.now() - hiddenAtRef.current
            : 0
        hiddenAtRef.current = null

        // ── Fast-forward the queued events ──────────────────────────────────
        //
        // Strategy: the server kept running while we were hidden. We don't want
        // to replay every animation at normal speed (that would take minutes).
        // Instead we:
        //   1. Drop stale events that are superseded by a later SYNC_STATE
        //   2. For TIMER_START events adjust the remaining time by elapsed gap
        //   3. Keep all state-mutation events so the reducer stays consistent
        //   4. Kill GSAP tweens so no stale animations play back
        //   5. Request a SYNC_STATE from the server as the authoritative truth

        gsap.killTweensOf("*")

        // Adjust or drop queued events based on elapsed hidden time
        const hiddenSeconds = hiddenDurationMs / 1000
        const adjustedQueue = []

        for (const event of eventQueueRef.current) {
            if (TIMER_EVENTS.has(event.type) && event.payload?.time != null) {
                const adjusted = event.payload.time - hiddenSeconds
                if (adjusted <= 0) {
                    // Timer already expired while hidden — drop it entirely;
                    // the server will send the authoritative follow-up event
                    continue
                }
                adjustedQueue.push({
                    ...event,
                    payload: { ...event.payload, time: Math.floor(adjusted) }
                })
            } else if (DROPPABLE_STALE_EVENTS.has(event.type)) {
                // Keep only the LAST occurrence of each droppable event type
                // so we reflect final server state without replaying intermediates
                const lastIdx = adjustedQueue.findLastIndex(e => e.type === event.type)
                if (lastIdx !== -1) {
                    adjustedQueue[lastIdx] = event // replace with newer version
                } else {
                    adjustedQueue.push(event)
                }
            } else {
                adjustedQueue.push(event)
            }
        }

        eventQueueRef.current = adjustedQueue

        if (tableRef.current) {
            tableRef.current.passToChild("handleStopGameTimer")
        }

        // Resume processing
        isProcessingRef.current = false
        processNextEvent()

        // Ask server for authoritative state; it will enqueue a fresh snapshot
        // props.network.sendof({ type: "SYNC_STATE" })
    }

    // ─── Queue mechanics ─────────────────────────────────────────────────────

    const enqueueEvent = (event) => {
        eventQueueRef.current.push(event)
        // Only kick off processing when the tab is visible and queue is idle
        if (!document.hidden) {
            processNextEvent()
        }
    }

    const processNextEvent = async () => {
        if (isProcessingRef.current) return
        if (!eventQueueRef.current.length) return

        isProcessingRef.current = true
        const event = eventQueueRef.current.shift()
        await executeEvent(event)
        isProcessingRef.current = false

        processNextEvent()
    }

    const executeEvent = (event) => {
        return new Promise((resolve) => {
            const finish = () => resolve()

            const dataType = {
                TABLE_INFO_RESPONSE: (event) => {
                    lobbbyRef.current?.setTables(event.payload)
                    finish()
                },

                JOIN_TABLE_SUCCESS: (event) => {
                    let tableId = event.payload.tableId
                    localStorage.setItem("tableId", JSON.stringify(tableId))
                    let currentTableDetails = lobbbyRef.current?.getTableDetails(tableId)
                    localStorage.setItem("currentTableDetails", JSON.stringify(currentTableDetails))
                    setCurrentTableId(tableId)
                    finish()
                },

                JOIN_TABLE_FAILED: (event) => {
                    localStorage.removeItem("tableId")
                    getLobbyInfo()
                    alert(JSON.stringify(event.payload.reason))
                    finish()
                },

                LEAVE_TABLE_SUCCESS: (event) => {
                    setCurrentTableDetails(undefined)
                    setCurrentTableId(undefined)
                    getLobbyInfo()
                    localStorage.removeItem("tableId")
                    localStorage.removeItem("currentTableDetails")
                    finish()
                },

                LEAVE_TABLE_FAILED: (event) => {
                    setCurrentTableDetails(undefined)
                    setCurrentTableId(undefined)
                    getLobbyInfo()
                    localStorage.removeItem("tableId")
                    localStorage.removeItem("currentTableDetails")
                    // alert(JSON.stringify(event.payload.reason))
                    finish()
                },

                DUPLECATE_CONNECTION: (event) => {
                    alert(JSON.stringify(event.payload.reason))
                    finish()
                },

                DEAL_CARDS: (event) => {
                    if (!tableRef.current) { finish(); return }
                    tableRef.current.passToChild("handleDealTrucoCards", event.payload, finish)
                },

                PLAYER_ACTIONS: (event) => {
                    if (!tableRef.current) { finish(); return }
                    tableRef.current.passToChild("setPlayerActionButtons", event.payload, finish)
                },

                PLAYER_ACTION_MESSAGE: (event) => {
                    if (!tableRef.current) { finish(); return }
                    tableRef.current.passToChild("handlePlayerActions", event.payload, finish)
                },

                GAME_RESULT: (event) => {
                    if (!tableRef.current) { finish(); return }
                    tableRef.current.passToChild("checkGameResult", event.payload, finish)
                },

                PLAYER_DETAILS: (event) => {
                    if (!tableRef.current) { finish(); return }
                    tableRef.current.passToChild("setPlayerDetails", event.payload, finish)
                },

                MATCH_START: (event) => {
                    if (!tableRef.current) { finish(); return }
                    tableRef.current.passToChild("setMatchStart", event.payload, finish)
                },

                PLAY_CARD_MESSAGE: (event) => {
                    if (!tableRef.current) { finish(); return }
                    tableRef.current.passToChild("handlePlayerDrawCard", event.payload, finish)
                },

                TRICK_WINNER: (event) => {
                    if (!tableRef.current) { finish(); return }
                    tableRef.current.passToChild("handleTrickWinner", event.payload, finish)
                },

                TRICK_DRAW: (event) => {
                    if (!tableRef.current) { finish(); return }
                    tableRef.current.passToChild("handleTrickDraw", event.payload, finish)
                },

                MATCH_WINNER: (event) => {
                    if (!tableRef.current) { finish(); return }
                    tableRef.current.passToChild("handleMatchWinner", event.payload, finish)
                },

                GAME_WINNER: (event) => {
                    if (!tableRef.current) { finish(); return }
                    tableRef.current.passToChild("handleGameWinner", event.payload, finish)
                },

                TIMER_START: (event) => {
                    if (!tableRef.current) { finish(); return }
                    tableRef.current.passToChild("handleTimerStart", event.payload, finish)
                },

                ENVIDO_RESULT: (event) => {
                    if (!tableRef.current) { finish(); return }
                    tableRef.current.passToChild("handleEnvidoResult", event.payload, finish)
                },

                GAME_RESTART_REQUEST: (event) => {
                    if (!tableRef.current) { finish(); return }
                    tableRef.current.passToChild("handleRestartRequest", event.payload, finish)
                },

                SESSION_EXPIRED: (event) => {
                    if (!tableRef.current) { finish(); return }
                    tableRef.current.passToChild("handleSessionExpired", event.payload, finish)
                }
            }

            if (dataType[event?.type]) {
                dataType[event.type](event)
            } else {
                finish()
            }
        })
    }

    const handleSocketData = (event) => {
        if (event.type === "SESSION_EXPIRED") {
            // Immediately flush everything — session is dead
            isProcessingRef.current = false
            eventQueueRef.current = []
            setCurrentTableDetails(undefined)
            setCurrentTableId(undefined)
            localStorage.removeItem("tableId")
            localStorage.removeItem("currentTableDetails")
            props.setPopUp(event.payload)
            return
        }
        if (event.type === "LEAVE_TABLE_SUCCESS") {
            isProcessingRef.current = false
            eventQueueRef.current = []
            setCurrentTableDetails(undefined)
            setCurrentTableId(undefined)
            localStorage.removeItem("tableId")
            localStorage.removeItem("currentTableDetails")
            executeEvent(event)
        }
        enqueueEvent(event)
    }

    const getLobbyInfo = () => {
        props.network.sendof({ type: "GAME_LOBBY", payload: {} })
    }

    return (
        <div className="Truco_game" id="truco_game">
            <div className="app_container">
                {currentTableId
                    ? <TableScreen sendOf={props.network?.sendof} ref={tableRef} currentTableDetails={currentTableDetails} />
                    : <LobbyScreen sendOf={props.network.sendof} ref={lobbbyRef} />
                }
            </div>
        </div>
    )
})

export default memo(Truco)
